import { FaDownload, FaEraser, FaSearch } from "react-icons/fa";
import Button from "../../../components/button/button";
import { Card } from "../../../components/card/card";
import InputForm from "../../../components/input/input-form";
import SelectComponent from "../../../components/select/select-component";
import { Template } from "../../../components/template/template";
import { AgGridReact } from "ag-grid-react";
import AgGridUtils from "../../../utils/aggrid-utils";
import { useQuery } from "@tanstack/react-query";
import DownloadService from "../../../services/web/download-service";
import { useMemo, useRef, useState } from "react";

const initialFilter = {
    filter: [],
    directory: "all",
    type: "all",
    search: "",
};

const directoryOptions = [
    { value: "all", label: "Todos" },
    { value: "all_logged_users", label: "Todos os usuários" },
    { value: "public", label: "Público" },
]

export default function Download() {
    const [directory, setDirectory] = useState(null);
    const [type, setType] = useState(null);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState(initialFilter);
    
    const { data: typeOptions = [] } = useQuery({
        queryKey: ['download-types'],
        queryFn: async () => {
            const res = await DownloadService.types();
            return res?.data ?? [];
        },
        select: (data) => {
            const uniqTypes = [...new Set(data.map(t => String(t).trim()))].filter(t => t && t.toLowerCase() !== "all").map(t => ({ value: t, label: t }));
            return [{ value: "all", label: "Todos" }, ...uniqTypes];
        },
    });

    const pathKey = {
        public: "Público",
        users: "Pessoal",
        all_logged_users: "Todos os usuários",
    };

    const flattenFiles = (filesObj = {}) => {
        let i = 0;

        return Object.entries(filesObj).flatMap(([key, files]) => (Array.isArray(files) ? files : []).map((file) => ({
            ...file,
            i: ++i,
            key: file.path ?? key,
            pathLabel: pathKey[file.path ?? key] ?? (file.path ?? key),
        })));
    };

    const payload = useMemo(() => ({
        directory: filter.directory,
        type: filter.type,
        search: filter.search,
    }), [filter]);

    const { data: downloadList = [], isLoading } = useQuery({
        queryKey: ["download-list", payload],
        queryFn: async () => {
            const res = await DownloadService.list(payload);
            return flattenFiles(res?.data ?? {});
        }
    });

    const columnDefs = useMemo(() => ([
        {headerName: "#", field: "i" ,flex: 1},
        {headerName: "Arquivos", field: "name", flex: 2},
        {headerName: "Diretório", field: "pathLabel", flex: 2},
        {headerName: "Criação", field: "created" ,flex: 3},
        {headerName: "Tipo", field: "format" ,flex: 1},
        {headerName: "Tamanho", field: "size" ,flex: 2},
    ]), []);

    const onRowClicked = (e) => {
        const row = e.data;
        if (!row?.key || !row?.name) return;

        window.open(DownloadService.download(row.key, row.name));
    };
    
    const gridRef = useRef(null);
    
    const handleSearch = () => {
        setFilter((prev) => ({
            ...prev,
            directory: directory?.value,
            type: type?.value,
            search: search.trim(),
        }));
    };

    const handleClear = () => {
        setFilter(initialFilter);
        setDirectory("");
        setType("");
        setSearch("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
        if (e.key === "Escape") handleClear();
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Downloads</Template.Title>
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <SelectComponent 
                                label="Diretório"
                                classNameWrapper="flex-2"
                                options={directoryOptions}
                                value={directory}
                                onChange={setDirectory}
                            />
                            <SelectComponent 
                                label="Tipo"
                                classNameWrapper="flex-1"
                                options={typeOptions}
                                value={type}
                                onChange={setType}
                            />
                            <InputForm 
                                label="Arquivo"
                                classNameWrapper="flex-3"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <div className="flex gap-2">
                                <Button 
                                    target="primary-outline" 
                                    title="Limpar"
                                    onClick={handleClear}
                                >
                                    <FaEraser />
                                </Button>
                                <Button 
                                    title="Buscar"
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex gap-2 justify-start">
                        <Button 
                            className="px-10"
                            onClick={() => window.open("/repo/sentry-instalador.jar", "_blank")}
                            >
                            Instalador Desktop <FaDownload /> 
                        </Button>
                        <Button 
                            className="px-10"
                            onClick={() => window.open("https://store.sentry.com.br/", "_blank")}
                        >
                            Aplicativos móveis <FaDownload /> 
                        </Button>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Header>
                        <Card.Title>Downloads</Card.Title>
                    </Card.Header>
                    <Card.Body className="flex">
                        <div className="h-[calc(100vh-247px)] w-full">
                            <AgGridReact 
                                ref={gridRef}
                                rowData={downloadList}
                                columnDefs={columnDefs}
                                onRowClicked={onRowClicked}
                                animateRows={false}
                                suppressCellFocus
                                gridOptions={{ localeText: AgGridUtils.localization() }}
                                theme={AgGridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}